import { connectRefinementList } from "instantsearch.js/es/connectors";
import { runAfterFrame } from "./helpers";

const FALLBACK_EXTERIOR_COLORS: Record<string, string> = {
    blanc: "#FFFFFF",
    gris: "#808080",
    noir: "#000000",
    bleu: "#0000FF",
    vert: "#008000",
    violet: "#9933cc",
    rouge: "#FF0000",
    orange: "#FFA500",
    jaune: "#FFFF00",
    marron: "#b5651d",
    beige: "#F5F5DC",
    argent: "#C0C0C0",
    or: "#daa520",
    bronze: "#cd7f32",
};

function getColorHex(
    colorName: string,
    mapping: Record<string, string>,
    fallback: Record<string, string>,
): string {
    if (mapping[colorName]) return mapping[colorName];
    const lower = colorName.toLowerCase();
    if (fallback[lower]) return fallback[lower];
    for (const [key, value] of Object.entries(fallback)) {
        if (lower.includes(key)) return value;
    }
    return "#CCCCCC";
}

function isColorLight(hex: string): boolean {
    let r = 0,
        g = 0,
        b = 0;
    if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
        r = parseInt(hex[1] + hex[2], 16);
        g = parseInt(hex[3] + hex[4], 16);
        b = parseInt(hex[5] + hex[6], 16);
    }
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
}

export { FALLBACK_EXTERIOR_COLORS };

export function createColorRefinementWidget(
    containerSelector: string,
    attribute: string,
    colorMapping: Record<string, string>,
    fallbackColors: Record<string, string>,
    predefinedOrder: string[],
    filterOut?: string[],
) {
    const container = document.querySelector(containerSelector) as HTMLElement;
    if (!container) return null;

    const customRefinement = connectRefinementList<{ container: string }>(
        (renderOptions) => {
            const { items, refine } = renderOptions;

            let sorted = [...items];
            if (filterOut) {
                sorted = sorted.filter((i) => !filterOut.includes(i.value));
            }

            const predefined = predefinedOrder
                .map((name) => sorted.find((i) => i.label === name))
                .filter((i): i is (typeof sorted)[number] => i !== undefined);
            const others = sorted.filter(
                (i) => !predefinedOrder.includes(i.label),
            );
            sorted = [...predefined, ...others];

            container.innerHTML = `<div class="color-refinement-list"><div class="color-refinement-list__items">${sorted
                .map((item) => {
                    const hex = getColorHex(
                        item.label,
                        colorMapping,
                        fallbackColors,
                    );
                    const selected = item.isRefined
                        ? " color-refinement-list__item--selected"
                        : "";
                    const checkmark = item.isRefined
                        ? `<span class="color-refinement-list__checkmark${
                              isColorLight(hex)
                                  ? " color-refinement-list__checkmark--dark"
                                  : ""
                          }">✓</span>`
                        : "";
                    return `<button type="button" data-attr="listing-panel-option" class="color-refinement-list__item${selected}" title="${item.label}" data-value="${item.value}">
                            <span class="color-refinement-list__color-square" style="background-color:${hex}">${checkmark}</span>
                        </button>`;
                })
                .join("")}</div>${
                sorted.length === 0
                    ? '<span class="color-refinement-list__no-results">Aucun résultat</span>'
                    : ""
            }</div>`;

            container
                .querySelectorAll<HTMLButtonElement>(
                    ".color-refinement-list__item",
                )
                .forEach((btn) => {
                    btn.addEventListener("click", () => {
                        const val = btn.dataset.value;
                        if (val) runAfterFrame(() => refine(val));
                    });
                });
        },
    );

    return customRefinement({
        container: containerSelector,
        attribute,
        operator: "or" as const,
        limit: 100,
    });
}
